import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { JanelaService } from 'src/app/services/JanelaService/janela.service';
import { IJanela } from 'src/app/services/JanelaService/janela.model';
import Swal from 'sweetalert2';

/** Aceita HH:mm, HH:mm:ss (API) ou HHmm com 4 dígitos (máscara). */
function horarioJanelaValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const v = (control.value ?? '').toString().trim();
    if (!v) return null;
    let h: number;
    let min: number;
    const colon = v.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (colon) {
      h = parseInt(colon[1], 10);
      min = parseInt(colon[2], 10);
    } else if (/^\d{4}$/.test(v)) {
      h = parseInt(v.slice(0, 2), 10);
      min = parseInt(v.slice(2, 4), 10);
    } else {
      return { horarioInvalido: true };
    }
    if (h < 0 || h > 23 || min < 0 || min > 59) return { horarioInvalido: true };
    return null;
  };
}

@Component({
  selector: 'app-listagem-janela',
  templateUrl: './listagem-janela.component.html',
  styleUrls: ['./listagem-janela.component.scss']
})
export class ListagemJanelaComponent implements OnInit, OnDestroy {
  private subscription$ = new Subscription();
  private janelaService = inject(JanelaService);
  private fb = inject(FormBuilder);

  janelas: IJanela[] = [];
  isLoading: boolean = false;
  isModalOpen: boolean = false;

  form: FormGroup = this.fb.group({
    PK_JNLA: [0],
    HR_INIC: ['', [Validators.required, horarioJanelaValidator()]],
    HR_FIM: ['', [Validators.required, horarioJanelaValidator()]],
    FL_STTS: [true]
  });

  tableColumns: any[] = [];
  tableData: any[] = [];
  maxPages: number = 1;
  qtdOptions = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 100, label: '100' }
  ];

  ngOnInit(): void {
    this.setTableColumns();
    this.carregarJanelas();
  }

  setTableColumns(): void {
    // app-tabela / app-card usam `header` e `type` (ver listagem-agendamento); `label` + sem type não renderiza linhas.
    this.tableColumns = [
      { header: 'Início', key: 'HR_INIC', type: 'text' },
      { header: 'Fim', key: 'HR_FIM', type: 'text' },
      { header: 'Status', key: 'FL_STTS_DESC', type: 'text' },
      {
        header: 'Ações',
        key: 'acoes',
        type: 'buttons',
        buttons: [
          {
            label: 'Editar',
            img: 'assets/edit.svg',
            action: (item: IJanela) => this.abrirModal(item),
            condition: () => true
          },
          {
            label: 'Excluir',
            img: 'assets/delete.svg',
            action: (item: IJanela) => this.excluir(item.PK_JNLA),
            condition: () => true
          }
        ]
      }
    ];
  }

  carregarJanelas(): void {
    this.isLoading = true;
    this.subscription$.add(
      this.janelaService.getJanelas().subscribe({
        next: (res) => {
          const list = Array.isArray(res) ? res : [];
          this.janelas = list;
          this.tableData = list.map(j => ({
            ...j,
            FL_STTS_DESC: j.FL_STTS ? 'Ativo' : 'Inativo'
          }));
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.janelas = [];
          this.tableData = [];
        }
      })
    );
  }

  /** Normaliza para HH:mm (5 caracteres), compatível com coluna da API. */
  private toHHmm(v: string | undefined | null): string {
    if (v == null || v === '') return '';
    const s = String(v).trim();
    if (/^\d{4}$/.test(s)) {
      const h = Math.min(23, Math.max(0, parseInt(s.slice(0, 2), 10)));
      const min = Math.min(59, Math.max(0, parseInt(s.slice(2, 4), 10)));
      return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
    }
    const m = s.match(/^(\d{1,2}):(\d{2})/);
    if (!m) return s.slice(0, 5);
    const h = Math.min(23, Math.max(0, parseInt(m[1], 10)));
    const min = Math.min(59, Math.max(0, parseInt(m[2], 10)));
    return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
  }

  abrirModal(janela?: IJanela): void {
    if (janela) {
      this.form.patchValue({
        ...janela,
        HR_INIC: this.toHHmm(janela.HR_INIC),
        HR_FIM: this.toHHmm(janela.HR_FIM)
      });
    } else {
      this.form.reset({
        PK_JNLA: 0,
        HR_INIC: '',
        HR_FIM: '',
        FL_STTS: true
      });
    }
    this.isModalOpen = true;
  }

  fecharModal(): void {
    this.isModalOpen = false;
  }

  salvar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const raw = this.form.getRawValue() as IJanela;
    const payload: IJanela = {
      ...raw,
      HR_INIC: this.toHHmm(raw.HR_INIC),
      HR_FIM: this.toHHmm(raw.HR_FIM)
    };
    this.subscription$.add(
      this.janelaService.saveJanela(payload).subscribe({
        next: () => {
          this.isLoading = false;
          this.fecharModal();
          this.carregarJanelas();
          Swal.fire('Sucesso!', 'Janela salva com sucesso.', 'success');
        },
        error: () => { this.isLoading = false; }
      })
    );
  }

  excluir(id: number): void {
    Swal.fire({
      title: 'Tem certeza?',
      text: 'Esta ação não pode ser revertida!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir!',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.isLoading = true;
        this.subscription$.add(
          this.janelaService.deleteJanela(id).subscribe({
            next: () => {
              this.isLoading = false;
              this.carregarJanelas();
              Swal.fire('Excluído!', 'A janela foi removida.', 'success');
            },
            error: () => { this.isLoading = false; }
          })
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }
}
